import { ReactNode, useEffect, useState } from 'react';
import { FaqSection } from '../../../../../api/types/faq-content-api';
import { RouteComponentProps } from 'react-router-dom';
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { Block, BLOCKS, Inline, INLINES } from '@contentful/rich-text-types';
import HelpCenterBreadcrumbs from '../Breadcrumb/HelpCenterBreadcrumbs';
import { Store } from '../../../../../types/store';
import { buildQuestionPageData } from './dataHelper';
import { IFaqQuestionPageData } from './models';
import { formatContentfulData } from './formatHelper';
import { redirectToHelpCenter } from '../../../utils';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { BreadcrumbSeoScript } from '../Breadcrumb/BreadcrumbSeoScript';
import { useMuiMediaQuery } from '../../../../../utilities/hooks/useMuiMediaQuery';
import RelatedQuestions from '../Shared/RelatedQuestions';
import OtherTopics from '../Shared/OtherTopics';
import NeedMoreHelp from '../Shared/NeedMoreHelp';
import QuestionPageSecondaryHeader from './QuestionPageSecondaryHeader';

export interface IFaqQuestionPageProps {
    location: RouteComponentProps['location'];
    allSections: FaqSection[] | undefined;
    nearestStoreId: Store['Id'] | undefined;
}

interface FaqParagraphProps {
    children: React.ReactNode;
}

const FaqParagraph = ({ children }: FaqParagraphProps) => {
    const { isMobile } = useMuiMediaQuery();
    return (
        <Typography color="textSecondary" variant={isMobile ? 'body1' : 'bodyLarge'} component="p">
            {children}
        </Typography>
    );
};

const options: Options = {
    renderNode: {
        [BLOCKS.PARAGRAPH]: (_: unknown, children: ReactNode) => <FaqParagraph>{children}</FaqParagraph>,
        [INLINES.HYPERLINK]: (node: Block | Inline, children: ReactNode) =>
            node?.data?.uri ? (
                <Link color="textSecondary" href={node?.data?.uri}>
                    {children}
                </Link>
            ) : (
                <></>
            ),
    },
};

type PlainTextNode = { value?: string; content?: PlainTextNode[] };

const extractPlainText = (node: PlainTextNode): string => {
    if (node.value) return node.value;
    if (node.content) return node.content.map(extractPlainText).join('');
    return '';
};

export const FaqQuestionPage = ({ location, allSections, nearestStoreId }: IFaqQuestionPageProps) => {
    const [pageData, setPageData] = useState<IFaqQuestionPageData>();
    const { isMobile } = useMuiMediaQuery();

    const sectionPath = location.pathname.split('/');

    useEffect(() => {
        // set all page data based on the url
        if (allSections && !pageData) {
            const pageData = buildQuestionPageData(allSections, sectionPath);
            pageData?.faq ? setPageData(pageData) : redirectToHelpCenter();
        }

        nearestStoreId && formatContentfulData(nearestStoreId);
    }, [allSections, nearestStoreId, pageData, sectionPath]);

    useEffect(() => {
        const title = pageData?.faq?.seoTags?.title;
        if (title) {
            document.title = title;
        }
    }, [pageData?.faq?.seoTags?.title]);

    if (!pageData?.faq) {
        return <></>;
    }
    const {
        breadcrumb,
        relatedQuestions,
        otherTopics,
        faq: { question, answer },
    } = pageData;

    const faqPageSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
            {
                '@type': 'Question',
                name: question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: extractPlainText(answer as PlainTextNode),
                },
            },
        ],
        publisher: {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            legalName: 'CarMax',
            url: 'https://www.carmax.com/',
            logo: 'https://www.carmax.com/css/img/carmax.png',
            sameAs: [
                'https://www.twitter.com/CarMax',
                'https://www.facebook.com/CarMax',
                'https://www.linkedin.com/company/carmax',
            ],
            contactPoint: {
                '@type': 'ContactPoint',
                telephone: '+1-800-519-1511',
                contactType: 'Customer Service',
                areaServed: 'US',
                contactOption: 'http://schema.org/TollFree',
            },
        },
        isPartOf: {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'CarMax',
            url: 'https://www.carmax.com/',
        },
        url: `https://www.carmax.com${location.pathname.startsWith('/') ? location.pathname : `/${location.pathname}`}`,
    };

    return (
        <Container id="page--faq-question" sx={{ pt: 2 }} maxWidth="md" data-testid="page-content">
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPageSchema) }} />
            {breadcrumb && <BreadcrumbSeoScript breadcrumb={breadcrumb} />}
            {breadcrumb && <HelpCenterBreadcrumbs breadcrumb={breadcrumb} />}
            <Stack spacing={8} sx={{ mt: { xs: 1, md: 3 }, pb: { xs: 4.5, md: 10 } }}>
                <Stack spacing={3}>
                    <Typography component="h1" variant={isMobile ? 'h5' : 'h4'} color="textPrimary">
                        {question}
                    </Typography>
                    {documentToReactComponents(answer, options)}
                </Stack>
                <RelatedQuestions
                    spacing={2}
                    relatedQuestions={relatedQuestions}
                    title={<QuestionPageSecondaryHeader>Related questions</QuestionPageSecondaryHeader>}
                />
                <OtherTopics otherTopics={otherTopics} />
                <NeedMoreHelp />
            </Stack>
        </Container>
    );
};
